import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle, XCircle, Slash, Calendar, FileText, Send, Download, RefreshCw, Edit2 } from "react-feather";
import toast from "react-hot-toast";
import DOMPurify from "dompurify";
import {
  useGetQuotationQuery, useApproveQuotationMutation, useRejectQuotationMutation, useCancelQuotationMutation,
  useResendQuotationDocumentMutation, useLazyGetQuotationDocumentPdfQuery,
} from "../quotationsApi";
import LineItemsEditor, { emptyRow } from "../../../stock/components/LineItemsEditor";
import { QUOTATION_VAT_PERCENT } from "../quotationVat";
import { useFillMissingUnits } from "../../../stock/components/lineUnits";
import DiscountFields, { DiscountedTotalsStrip, computeQuotationTotals, discountRequest } from "../../components/DiscountFields";
import StatusBadge from "../../../../components/common/StatusBadge";
import ActorName from "../../../../components/common/ActorName";
import ReasonPrompt from "../../../../components/common/ReasonPrompt";
import CollapsibleCard, { PANEL_ACCENT } from "../../components/CollapsibleCard";
import QuotationDeliveryModal from "../components/QuotationDeliveryModal";
import HistoryTimeline from "../../../../components/common/HistoryTimeline";
import { buildQuotationHistory } from "../buildQuotationHistory";
import { buildQuotationPrintProps } from "../buildQuotationPrintProps";
import { QUOTATION_STATUS_CLASS, QUOTATION_STATUS_LABEL } from "../quotationStatusMaps";
import {
  useCanEditQuotation, useCanEditApprovedQuotation, useCanApproveQuotation, useCanCancelQuotation, useCanSendQuotation,
} from "../useQuotationPermissions";
import { useClaims } from "../../../auth/useClaims";
import { useGetCompanyInfoQuery } from "../../../settings/settingsApi";
import { useGetServiceTypesQuery } from "../../../masters/serviceTypes/serviceTypesApi";
import { useGetEnquiryQuery } from "../../salesApi";
import { useLazyGetGeneralManagerSignatureQuery } from "../../../masters/employees/employeesApi";
import { blobToDataUrl } from "../../../stock/lpo/buildLpoPrintProps";
import QuotationLineLabel from "../components/QuotationLineLabel";
import { paths } from "../../../../routes/paths";

const money = (n) => (n == null ? "-" : Number(n).toFixed(2));

const lineToRow = (l) => ({
  ...emptyRow(),
  key: `line-${l.lineId}`,
  lineType: l.lineType,
  productCode: l.productCode || "",
  productName: l.description,
  unit: l.unit || "",
  qty: String(l.qty),
  rate: String(l.rate),
  vatPercent: String(l.vatPercent),

  productGroupId: l.lineType === "FREETEXT" && l.productGroupId != null ? String(l.productGroupId) : "",
});

const toLineRequest = (row) => ({
  lineType: row.lineType || "PRODUCT",
  productCode: row.lineType === "FREETEXT" ? null : row.productCode || null,
  description: row.productName || row.productGroupName || "", // a group line with no wording prints as the group name
  qty: Number(row.qty) || 0,
  unit: row.unit || null,
  rate: Number(row.rate) || 0,
  vatPercent: Number(row.vatPercent) || 0,
  // Batch185 - only a free-text line carries a group tag; a product has its own group.
  productGroupId: row.lineType === "FREETEXT" && row.productGroupId ? Number(row.productGroupId) : null,
});

const QuotationDetail = () => {
  const { recordGuid } = useParams();
  const navigate = useNavigate();
  const { userId } = useClaims();
  const canEdit = useCanEditQuotation();
  const canEditApproved = useCanEditApprovedQuotation();
  const canApprove = useCanApproveQuotation();
  const canCancel = useCanCancelQuotation();
  const canSend = useCanSendQuotation();

  const { data: quotation, isLoading, isError, error } = useGetQuotationQuery(recordGuid);
  const { data: companyInfo } = useGetCompanyInfoQuery(quotation?.branchId, { skip: !quotation });
  const { data: serviceTypes = [] } = useGetServiceTypesQuery(
    { branchId: quotation?.branchId, isActive: true },
    { skip: !quotation }
  );
  // Only fetched when linked to an enquiry, purely to name the services that
  // enquiry was raised for on the print heading (see buildQuotationPrintProps).
  const { data: linkedEnquiry } = useGetEnquiryQuery(quotation?.salesEnquiryRecordGuid, {
    skip: !quotation?.salesEnquiryRecordGuid,
  });
  const [approveQuotation, { isLoading: isApproving }] = useApproveQuotationMutation();
  const [rejectQuotation] = useRejectQuotationMutation();
  const [cancelQuotation] = useCancelQuotationMutation();
  const [resendDocument, { isLoading: isResending }] = useResendQuotationDocumentMutation();
  const [fetchDocumentPdf] = useLazyGetQuotationDocumentPdfQuery();
  const fillMissingUnits = useFillMissingUnits();

  const [showApprovePanel, setShowApprovePanel] = useState(false);
  const [approveRows, setApproveRows] = useState([]);
  // Discount while approving (Batch163) - opens prefilled with the quotation's own.
  const [approveDiscountPercent, setApproveDiscountPercent] = useState("");
  const [approveDiscountAmount, setApproveDiscountAmount] = useState("");
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);

  // The approver's seal/signature ("Approval Signature" Employee Document,
  // Batch92) - same fetch-once-and-convert-to-data-URI approach as
  // LpoDetail.jsx (the print popup can't carry an auth header itself).
  // The GENERAL MANAGER's seal, not the approver's (changed 2026-09-09 on
  // direct instruction). A quotation goes out under the company's authority, so
  // the seal is the GM's whoever happened to approve this particular one. The
  // two are the same person today, which is exactly why this had to be made
  // explicit now rather than left to coincide.
  //
  // Fetched unconditionally - it does not depend on the quotation at all. A 404
  // (nobody holds the designation, or no signature uploaded) just means the
  // document prints without a seal.
  const [fetchGeneralManagerSignature] = useLazyGetGeneralManagerSignatureQuery();
  const [signatureImageUrl, setSignatureImageUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchGeneralManagerSignature()
      .unwrap()
      .then(blobToDataUrl)
      .then((url) => { if (!cancelled) setSignatureImageUrl(url); })
      .catch(() => { if (!cancelled) setSignatureImageUrl(null); });
    return () => { cancelled = true; };
  }, [fetchGeneralManagerSignature]);

  if (isLoading) return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;
  if (isError || !quotation) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link to={paths.quotations} className="d-inline-flex align-items-center mb-3"><ArrowLeft size={16} className="me-1" /> Back to Quotations</Link>
          <div className="alert alert-danger">Could not load this quotation ({error?.message || "server error"}).</div>
        </div>
      </div>
    );
  }

  const isSubmitted = quotation.status === "SUBMITTED";
  const isApproved = quotation.status === "APPROVED";
  // By user id - names are display text and two people can share one.
  const isOwner = userId != null && quotation.createdBy === userId;
  const canApproveNow = isSubmitted && canApprove && !isOwner;
  const canRejectNow = isSubmitted && canApprove && !isOwner;
  const canCancelNow = isSubmitted && canCancel && isOwner;
  const canEditNow = (isSubmitted && canEdit) || (isApproved && canEditApproved);

  const openApprovePanel = () => {
    const submittedRows = quotation.lines.map(lineToRow);
    setApproveRows(submittedRows);
    // Only the editable copy gets its blank units resolved - "Approve as
    // submitted" below deliberately re-derives from quotation.lines, so it
    // still sends exactly what was submitted.
    fillMissingUnits(submittedRows, quotation.branchId).then((filled) => {
      if (filled !== submittedRows) setApproveRows(filled);
    });
    setApproveDiscountPercent(quotation.discountPercent ? String(quotation.discountPercent) : "");
    setApproveDiscountAmount(!quotation.discountPercent && quotation.discountAmount ? String(quotation.discountAmount) : "");
    setShowApprovePanel(true);
  };

  const handleApprove = async (useEditedLines) => {
    // "As submitted" is about the LINES; the discount box is sent either way.
    const rowsForTotals = useEditedLines ? approveRows : quotation.lines.map(lineToRow);
    if (computeQuotationTotals(rowsForTotals, approveDiscountPercent, approveDiscountAmount).exceeds) {
      return toast.error("The discount is more than the sub total");
    }
    const discount = discountRequest(approveDiscountPercent, approveDiscountAmount);
    try {
      await approveQuotation({
        recordGuid,
        lines: useEditedLines ? approveRows.map(toLineRequest) : null,
        // Always explicit (0 = none) - the panel opened prefilled.
        discountPercent: discount.discountPercent ?? 0,
        discountAmount: discount.discountAmount ?? 0,
      }).unwrap();
      toast.success("Quotation approved");
      setShowApprovePanel(false);
    } catch (e) {
      toast.error(e?.message || "Could not approve the quotation");
    }
  };

  const handleReject = async (rejectionReason) => {
    try {
      await rejectQuotation({ recordGuid, rejectionReason }).unwrap();
      toast.success("Quotation rejected");
      setShowRejectForm(false);
    } catch (e) {
      toast.error(e?.message || "Could not reject the quotation");
    }
  };

  const handleCancel = async (cancelReason) => {
    try {
      await cancelQuotation({ recordGuid, cancelReason }).unwrap();
      toast.success("Quotation cancelled");
      setShowCancelForm(false);
    } catch (e) {
      toast.error(e?.message || "Could not cancel the quotation");
    }
  };

  const handleViewDocument = async () => {
    try {
      const blob = await fetchDocumentPdf(recordGuid).unwrap();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e?.message || "No document has been generated yet");
    }
  };

  const handleResend = async () => {
    try {
      await resendDocument(recordGuid).unwrap();
      toast.success("Delivery re-attempted");
    } catch (e) {
      toast.error(e?.message || "Could not resend the quotation");
    }
  };

  const approveTotals = computeQuotationTotals(approveRows, approveDiscountPercent, approveDiscountAmount);
  const printProps = buildQuotationPrintProps(quotation, companyInfo, signatureImageUrl, serviceTypes, linkedEnquiry?.serviceTypes || []);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <Link to={paths.quotations} className="d-inline-flex align-items-center mb-2"><ArrowLeft size={16} className="me-1" /> Back to Quotations</Link>
            <h4>{quotation.quotationNumber} <StatusBadge status={quotation.status} classMap={QUOTATION_STATUS_CLASS} labelMap={QUOTATION_STATUS_LABEL} /></h4>
            <h6>{quotation.branchCode}</h6>
          </div>
          {canEditNow && (
            <div className="page-btn">
              <button type="button" className="btn btn-outline-primary d-inline-flex align-items-center gap-1" onClick={() => navigate(paths.quotationEdit(recordGuid))}>
                <Edit2 size={14} /> Edit
              </button>
            </div>
          )}
        </div>

        <div className="card mb-3">
          <div className="card-body d-flex align-items-center gap-3">
            <span className="master-detail-avatar master-detail-avatar-lg">{(quotation.customerName || "?").trim().slice(0, 2).toUpperCase()}</span>
            <div className="flex-grow-1">
              <h5 className="mb-0">{quotation.customerName}</h5>
              <div className="d-flex flex-wrap gap-3 mt-2 text-muted small">
                <span className="d-inline-flex align-items-center"><Calendar size={14} className="me-1" />{new Date(quotation.quotationDate).toLocaleDateString()}</span>
                {quotation.salesEnquiryNumber && <span>Linked: {quotation.salesReferenceNumber || quotation.salesEnquiryNumber}</span>}
                {quotation.prototypeName && <span>From prototype: {quotation.prototypeName}</span>}
                {quotation.salespersonName && (
                  <span>Contact: {quotation.salespersonName}{quotation.salespersonPhone ? ` (${quotation.salespersonPhone})` : ""}</span>
                )}
                <span>Total: <strong>{money(quotation.totalAmount)}</strong></span>
              </div>
            </div>
          </div>
        </div>

        {quotation.status === "REJECTED" && quotation.rejectionReason && (
          <div className="alert alert-danger">Rejected by {quotation.rejectedByName}: {quotation.rejectionReason}</div>
        )}
        {quotation.status === "CANCELLED" && (
          <div className="alert alert-secondary">Cancelled{quotation.cancelReason ? `: ${quotation.cancelReason}` : ""}</div>
        )}
        {quotation.lastEditedAfterApprovalByName && (
          <div className="alert alert-info">
            Edited after approval by {quotation.lastEditedAfterApprovalByName} on {new Date(quotation.lastEditedAfterApprovalDate).toLocaleString()}
          </div>
        )}

        <CollapsibleCard
          title="Line Items"
          icon={FileText}
          accent={PANEL_ACCENT.document}
          hasData={quotation.lines.length > 0}
          defaultOpen
          forceOpen={showApprovePanel || showRejectForm}
          count={quotation.lines.length}
          summary={`Total ${money(quotation.totalAmount)}${quotation.discountAmount > 0 ? " · discounted" : ""}`}
          actions={
            <>
              {canApproveNow && !showApprovePanel && (
                <button type="button" className="btn btn-success btn-sm d-inline-flex align-items-center gap-1" onClick={openApprovePanel}>
                  <CheckCircle size={14} /> Approve
                </button>
              )}
              {canRejectNow && (
                <button type="button" className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1" onClick={() => setShowRejectForm((s) => !s)}>
                  <XCircle size={14} /> Reject
                </button>
              )}
            </>
          }
        >
          {showRejectForm && <ReasonPrompt label="Rejection Reason" confirmLabel="Reject" onConfirm={handleReject} onCancel={() => setShowRejectForm(false)} />}

          {showApprovePanel ? (
            <>
              <div className="alert alert-info small">
                Edit rate/qty/unit below if needed, then approve. Totals recompute from whatever is shown here.
              </div>
              <LineItemsEditor
                branchId={quotation.branchId}
                rows={approveRows}
                onChange={setApproveRows}
                allowFreeText
                rateField="salesRate"
                columns={{ showRate: true, showVat: true, amountIncludesVat: true }}
                fixedVatPercent={QUOTATION_VAT_PERCENT}
              />
              <div className="row mt-3">
                <DiscountFields
                  percent={approveDiscountPercent}
                  amount={approveDiscountAmount}
                  onPercentChange={setApproveDiscountPercent}
                  onAmountChange={setApproveDiscountAmount}
                />
              </div>
              <div className="mb-3">
                <DiscountedTotalsStrip totals={approveTotals} percent={approveDiscountPercent} />
              </div>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-success" onClick={() => handleApprove(true)} disabled={isApproving}>
                  Approve with these changes
                </button>
                <button type="button" className="btn btn-outline-success" onClick={() => handleApprove(false)} disabled={isApproving}>
                  Approve as submitted
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowApprovePanel(false)}>Cancel</button>
              </div>
            </>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead>
                    <tr><th>#</th><th>Description</th><th>Unit</th><th className="text-end">Qty</th><th className="text-end">Rate</th><th className="text-end">VAT %</th><th className="text-end">Amount</th></tr>
                  </thead>
                  <tbody>
                    {quotation.lines.map((l, idx) => (
                      <tr key={l.lineId}>
                        <td>{idx + 1}</td>
                        <td><QuotationLineLabel line={l} /></td>
                        <td>{l.unit || "-"}</td>
                        <td className="text-end">{l.qty}</td>
                        <td className="text-end">{money(l.rate)}</td>
                        <td className="text-end">{l.vatPercent}</td>
                        <td className="text-end">{money(l.lineAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* subTotal is the plain line total; vat/total are already net of
                  the discount (Batch163). */}
              <div
                className="d-flex flex-wrap align-items-center gap-4 px-3 py-2 rounded mt-3"
                style={{ background: "#EEF7F1", border: "1px solid #CDE8D6" }}
              >
                <span>Sub Total: <strong>{money(quotation.subTotal)}</strong></span>
                {quotation.discountAmount > 0 && (
                  <>
                    <span className="text-danger">
                      Discount{quotation.discountPercent ? ` (${Number(quotation.discountPercent)}%)` : ""}: <strong>-{money(quotation.discountAmount)}</strong>
                    </span>
                    <span>Net: <strong>{money(quotation.subTotal - quotation.discountAmount)}</strong></span>
                  </>
                )}
                <span>VAT: <strong>{money(quotation.vatAmount)}</strong></span>
                <span className="ms-auto" style={{ color: PANEL_ACCENT.money }}>
                  Total: <strong style={{ fontSize: 18 }}>{money(quotation.totalAmount)}</strong>
                </span>
              </div>
              {quotation.approvedByName && (
                <div className="text-muted small mt-2">Approved by <ActorName action="Approved" name={quotation.approvedByName} /> on {new Date(quotation.approvedDate).toLocaleString()}</div>
              )}
            </>
          )}
        </CollapsibleCard>

        {isApproved && (
          <CollapsibleCard
            title="Delivery"
            icon={Send}
            accent={PANEL_ACCENT.link}
            actions={
              canSend && (
                <button type="button" className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1" onClick={() => setShowDeliveryModal(true)}>
                  <Send size={14} /> Generate &amp; Send
                </button>
              )
            }
          >
            <div className="d-flex flex-wrap gap-3 align-items-center">
              <button type="button" className="btn btn-link p-0 d-inline-flex align-items-center gap-1" onClick={handleViewDocument}>
                <Download size={14} /> View Document
              </button>
              {canSend && (
                <button type="button" className="btn btn-link p-0 d-inline-flex align-items-center gap-1" onClick={handleResend} disabled={isResending}>
                  <RefreshCw size={14} /> {isResending ? "Resending..." : "Resend"}
                </button>
              )}
            </div>
          </CollapsibleCard>
        )}

        {canCancelNow && (
          <CollapsibleCard title="Cancel" icon={Slash} accent={PANEL_ACCENT.neutral}>
            {showCancelForm && (
              <ReasonPrompt label="Cancel Reason (optional)" confirmLabel="Cancel Quotation" required={false} onConfirm={handleCancel} onCancel={() => setShowCancelForm(false)} />
            )}
            {!showCancelForm && (
              <button type="button" className="btn btn-outline-danger d-inline-flex align-items-center gap-1" onClick={() => setShowCancelForm(true)}>
                <Slash size={14} /> Cancel Quotation
              </button>
            )}
          </CollapsibleCard>
        )}

        {quotation.remarks && (
          <div className="card mb-4">
            <div className="card-body">
              <h6 className="mb-2">Footer Notes</h6>
              <div className="small text-muted" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(quotation.remarks) }} />
            </div>
          </div>
        )}

        {/* Last block on the page on purpose: a reviewer wants the quotation
            itself first, and the chain of hands only if something looks wrong.
            Derived entirely from actor columns the record already carries, so
            this cost no endpoint and works for old quotations too. */}
        <HistoryTimeline entries={buildQuotationHistory(quotation)} />

        <QuotationDeliveryModal show={showDeliveryModal} onClose={() => setShowDeliveryModal(false)} docProps={printProps} quotation={quotation} />
      </div>
    </div>
  );
};

export default QuotationDetail;
